import React, { useEffect, useRef, useState } from 'react';
import { customerService, Company, CompanyAttachment, CustomerUser } from '../services/customerService';
import { organizationService, OrgMember } from '../services/organizationService';
import { projectStore } from '../lib/projectStore';
import { useAuth } from '../contexts/AuthContext';
import TaskDescriptionEditor from './TaskDescriptionEditor';
import '../styles/CustomerModal.css';

interface CompanyModalProps {
  company?: Company | null;
  onClose: () => void;
  onSaved: (company: Company) => void;
}

type SectionKey = 'users' | 'products' | 'support_tickets' | 'features' | 'subfeatures';
type TabKey = 'description' | 'insights' | 'history';

const CompanyModal: React.FC<CompanyModalProps> = ({ company, onClose, onSaved }) => {
  const { currentOrg } = useAuth();
  const isEdit = !!company;

  const [name, setName]           = useState(company?.name ?? '');
  const [details, setDetails]     = useState(company?.details ?? '');
  const [domain, setDomain]       = useState(company?.domain ?? '');
  const [empSize, setEmpSize]     = useState(company?.employee_size != null ? String(company.employee_size) : '');
  const [headquarters, setHq]     = useState(company?.headquarters ?? '');
  const [industry, setIndustry]   = useState(company?.industry ?? '');
  const [revenue, setRevenue]     = useState(company?.revenue != null ? String(company.revenue) : '');
  const [website, setWebsite]     = useState(company?.website ?? '');
  const [saving, setSaving]       = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>('description');
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(new Set());
  const [showMenu, setShowMenu]   = useState(false);

  // Owner = customer user
  const [ownerUser, setOwnerUser]             = useState<CustomerUser | null>(null);
  const [ownerQuery, setOwnerQuery]           = useState('');
  const [allCustomerUsers, setAllCustomerUsers] = useState<CustomerUser[]>([]);
  const [showOwnerSearch, setShowOwnerSearch] = useState(false);

  // Assignee = org member
  const [assignee, setAssignee]               = useState<OrgMember | null>(null);
  const [assigneeQuery, setAssigneeQuery]     = useState('');
  const [allMembers, setAllMembers]           = useState<OrgMember[]>([]);
  const [showAssigneeSearch, setShowAssigneeSearch] = useState(false);

  // Attachments
  const [attachments, setAttachments]         = useState<CompanyAttachment[]>([]);
  const [uploading, setUploading]             = useState(false);
  const [lightboxUrl, setLightboxUrl]         = useState<string | null>(null);

  // Linked users for section count
  const [linkedContacts, setLinkedContacts] = useState<CustomerUser[]>([]);

  const fileInputRef     = useRef<HTMLInputElement>(null);
  const menuRef          = useRef<HTMLDivElement>(null);
  const ownerInputRef    = useRef<HTMLInputElement>(null);
  const assigneeInputRef = useRef<HTMLInputElement>(null);

  // Load all customer users (for owner picker) + linked contacts (for section count)
  useEffect(() => {
    customerService.getCustomerUsers(projectStore.getProjectId())
      .then(users => {
        setAllCustomerUsers(users);
        if (company) {
          setLinkedContacts(users.filter(u => u.company_id === company.id));
          if (company.owner_customer_user_id) {
            const match = users.find(u => u.id === company.owner_customer_user_id);
            if (match) setOwnerUser(match);
          }
        }
      })
      .catch(() => {});
  }, [company]);

  // Load org members for assignee search
  useEffect(() => {
    if (!currentOrg) return;
    organizationService.getMembersWithProfiles(currentOrg.id)
      .then(members => {
        setAllMembers(members);
        if (company?.assignee_id) {
          const match = members.find(m => m.user_id === company.assignee_id);
          if (match) setAssignee(match);
        }
      })
      .catch(() => {});
  }, [currentOrg, company]);

  // Load existing attachments when editing
  useEffect(() => {
    if (company?.id) {
      customerService.getAttachments(company.id)
        .then(setAttachments)
        .catch(() => {});
    }
  }, [company?.id]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const h = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showMenu]);

  // Close owner search on outside click
  useEffect(() => {
    if (!showOwnerSearch) return;
    const h = (e: MouseEvent) => {
      if (ownerInputRef.current && !ownerInputRef.current.parentElement?.contains(e.target as Node)) setShowOwnerSearch(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showOwnerSearch]);

  // Close assignee search on outside click
  useEffect(() => {
    if (!showAssigneeSearch) return;
    const h = (e: MouseEvent) => {
      if (assigneeInputRef.current && !assigneeInputRef.current.parentElement?.contains(e.target as Node)) setShowAssigneeSearch(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showAssigneeSearch]);

  const ownerMatches = allCustomerUsers.filter(u => {
    const q = ownerQuery.trim().toLowerCase();
    if (!q) return true;
    return u.name.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q);
  }).slice(0, 8);

  const assigneeMatches = allMembers.filter(m => {
    const q = assigneeQuery.trim().toLowerCase();
    if (!q) return true;
    return (m.full_name ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q);
  }).slice(0, 8);

  const toggleSection = (key: SectionKey) => {
    setOpenSections(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !currentOrg) return;
    e.target.value = '';

    // For new companies, we save first then attach
    if (!company?.id) {
      alert('Please save the company first, then add attachments.');
      return;
    }

    setUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map(f => customerService.uploadAttachment(company.id, currentOrg.id, f))
      );
      setAttachments(prev => [...prev, ...uploaded]);
    } catch (err) {
      console.error('Upload failed:', err);
      alert(`Failed to upload: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAttachment = async (att: CompanyAttachment) => {
    if (!window.confirm(`Remove "${att.file_name}"?`)) return;
    try {
      await customerService.deleteAttachment(att);
      setAttachments(prev => prev.filter(a => a.id !== att.id));
    } catch (err) {
      console.error('Delete attachment failed:', err);
      alert('Failed to remove attachment.');
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { alert('Company name is required'); return; }
    setSaving(true);
    try {
      const extra = {
        owner_customer_user_id: ownerUser?.id ?? null,
        assignee_id:            assignee?.user_id ?? null,
        domain:                 domain.trim() || null,
        employee_size:          empSize ? Number(empSize) : null,
        headquarters:           headquarters.trim() || null,
        industry:               industry.trim() || null,
        revenue:                revenue ? Number(revenue) : null,
        website:                website.trim() || null,
      };

      let saved: Company;
      if (isEdit && company) {
        saved = await customerService.updateCompany(company.id, {
          name: name.trim(),
          details: details.trim(),
          ...extra,
        });
      } else {
        saved = await customerService.createCompany(
          name.trim(), details.trim(), projectStore.getProjectId(), extra
        );
      }
      onSaved(saved);
    } catch (err) {
      console.error('Error saving company:', err);
      alert('Failed to save company. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const sections: { key: SectionKey; label: string }[] = [
    { key: 'users',           label: 'Users' },
    { key: 'products',        label: 'Products' },
    { key: 'support_tickets', label: 'Support Tickets' },
    { key: 'features',        label: 'Features' },
    { key: 'subfeatures',     label: 'Subfeatures' },
  ];

  const sectionCounts: Record<SectionKey, number> = {
    users: linkedContacts.length, products: 0, support_tickets: 0, features: 0, subfeatures: 0,
  };

  const isImage = (type: string) => type.startsWith('image/');

  return (
    <>
      <div className="cum-modal-backdrop" onClick={() => !saving && onClose()}>
        <div className="inline-edit-form premium-task-modal cum-company-modal" onClick={e => e.stopPropagation()}>

          {/* ── Header ── */}
          <div className="task-modal-header">
            <div className="header-titles-new">
              <h3>{isEdit ? 'Edit Company' : 'Add Company'}</h3>
              <p className="header-subtitle-new">Manage company details and linked records</p>
            </div>
            <div className="cum-header-actions">
              <div className="cum-menu-wrapper" ref={menuRef}>
                <button className="task-modal-close cum-dots-btn" onClick={() => setShowMenu(p => !p)} title="More options" disabled={saving}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="5"  r="1.5"/>
                    <circle cx="12" cy="12" r="1.5"/>
                    <circle cx="12" cy="19" r="1.5"/>
                  </svg>
                </button>
                {showMenu && (
                  <div className="cum-context-menu">
                    <button className="cum-menu-item" onClick={() => { navigator.clipboard.writeText(window.location.href).catch(() => {}); setShowMenu(false); }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      Copy link
                    </button>
                    <div className="cum-menu-divider" />
                    <button className="cum-menu-item" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                      Duplicate
                    </button>
                    <button className="cum-menu-item cum-menu-item-muted" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Archive
                    </button>
                    <button className="cum-menu-item cum-menu-item-danger" onClick={() => setShowMenu(false)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                      Delete company
                    </button>
                  </div>
                )}
              </div>
              <button type="button" className="task-modal-close" onClick={onClose} disabled={saving}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          {/* ── Two-column content ── */}
          <div className="task-modal-content-layout">

            {/* LEFT COLUMN */}
            <div className="task-modal-main-column">

              <div className="form-group-new">
                <label className="section-label-new">Company Name <span style={{ color: '#dc2626' }}>*</span></label>
                <div className="title-input-wrapper-new">
                  <input
                    type="text"
                    className="task-title-input-new"
                    placeholder="Enter company name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              {/* Tabs */}
              <div className="cum-tabs cum-tabs-inline">
                {(['description', 'insights', 'history'] as TabKey[]).map(tab => (
                  <button key={tab} type="button" className={`cum-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {activeTab === 'description' && (
                <>
                  <div className="form-group-new">
                    <TaskDescriptionEditor
                      key={`company-${company?.id ?? 'new'}`}
                      htmlValue={details}
                      onChange={setDetails}
                      placeholder="Add a description — industry, address, notes…"
                    />
                  </div>

                  {/* Attachment previews */}
                  {attachments.length > 0 && (
                    <div className="cum-attachment-previews">
                      {attachments.map(att => (
                        <div key={att.id} className="cum-att-thumb">
                          {isImage(att.file_type) ? (
                            <img
                              src={att.url}
                              alt={att.file_name}
                              className="cum-att-img"
                              onClick={() => setLightboxUrl(att.url)}
                              title={att.file_name}
                            />
                          ) : (
                            <div className="cum-att-file" title={att.file_name}>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              <span className="cum-att-file-name">{att.file_name.length > 14 ? att.file_name.slice(0, 12) + '…' : att.file_name}</span>
                            </div>
                          )}
                          <button
                            type="button"
                            className="cum-att-remove"
                            onClick={() => handleRemoveAttachment(att)}
                            title="Remove"
                          >
                            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                      {uploading && (
                        <div className="cum-att-thumb cum-att-uploading">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round" className="cum-att-spinner"><path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeDasharray="40" strokeDashoffset="20"/></svg>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Attachment button */}
                  <div className="form-group-new">
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={handleFileChange} />
                    <div className="integration-buttons-grid-new">
                      <div className="integrations-grid-row-new">
                        <button type="button" className="integration-btn-new" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                          </svg>
                          {uploading ? 'Uploading…' : 'Attachment'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Linked sections */}
                  <div className="cum-sections-list">
                    {sections.map(({ key, label }) => {
                      const open  = openSections.has(key);
                      const count = sectionCounts[key];
                      return (
                        <div key={key} className="cum-section">
                          <button type="button" className="cum-section-header" onClick={() => toggleSection(key)}>
                            <svg className={`cum-section-chevron ${open ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6"/>
                            </svg>
                            <span className="cum-section-label">{label}</span>
                            <span className="cum-section-count">{count}</span>
                            <button type="button" className="cum-section-add" onClick={e => e.stopPropagation()} title={`Add ${label.toLowerCase()}`}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                              </svg>
                            </button>
                          </button>
                          {open && key === 'users' && linkedContacts.length > 0 && (
                            <div className="cum-section-body">
                              {linkedContacts.map(c => (
                                <div key={c.id} className="cum-section-row">
                                  <span className="cum-section-row-icon">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                  </span>
                                  <span className="cum-section-row-name">{c.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {open && count === 0 && (
                            <div className="cum-section-empty">No {label.toLowerCase()} linked yet</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {activeTab === 'insights' && (
                <div className="cum-insights-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>
                  <p>Insights will appear here once linked to this company.</p>
                </div>
              )}

              {activeTab === 'history' && (
                <div className="cum-insights-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  <p>Activity history will appear here.</p>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN */}
            <div className="task-modal-sidebar-column">

              {/* Assignee — org member */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Assignee</label>
                <div style={{ position: 'relative' }}>
                  {showAssigneeSearch ? (
                    <div className="cum-owner-search-box">
                      <input
                        ref={assigneeInputRef}
                        className="task-title-input-new cum-owner-input"
                        type="text"
                        placeholder="Search members…"
                        value={assigneeQuery}
                        onChange={e => setAssigneeQuery(e.target.value)}
                        autoFocus
                      />
                      <div className="cum-owner-dropdown">
                        <button type="button" className="cum-owner-option" onClick={() => { setAssignee(null); setAssigneeQuery(''); setShowAssigneeSearch(false); }}>
                          <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No assignee</span>
                        </button>
                        {assigneeMatches.length === 0 && assigneeQuery.trim() && <div className="cum-owner-empty">No members found</div>}
                        {assigneeMatches.map(m => (
                          <button key={m.id} type="button" className="cum-owner-option" onClick={() => { setAssignee(m); setAssigneeQuery(''); setShowAssigneeSearch(false); }}>
                            <span className="cum-owner-avatar">{(m.full_name || m.email || '?')[0].toUpperCase()}</span>
                            <div className="cum-owner-info">
                              <span className="cum-owner-name">{m.full_name || 'Unnamed'}</span>
                              <span className="cum-owner-email">{m.email}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => setShowAssigneeSearch(true)}>
                      {assignee ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-avatar cum-owner-avatar-sm">{(assignee.full_name || assignee.email || '?')[0].toUpperCase()}</span>
                          <span>{assignee.full_name || assignee.email}</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-circle" />
                          <span style={{ color: '#94a3b8' }}>Not assigned</span>
                        </div>
                      )}
                      <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Owner — customer user */}
              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Owner</label>
                <div style={{ position: 'relative' }}>
                  {showOwnerSearch ? (
                    <div className="cum-owner-search-box">
                      <input
                        ref={ownerInputRef}
                        className="task-title-input-new cum-owner-input"
                        type="text"
                        placeholder="Search users…"
                        value={ownerQuery}
                        onChange={e => setOwnerQuery(e.target.value)}
                        autoFocus
                      />
                      <div className="cum-owner-dropdown">
                        <button type="button" className="cum-owner-option" onClick={() => { setOwnerUser(null); setOwnerQuery(''); setShowOwnerSearch(false); }}>
                          <span style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No owner</span>
                        </button>
                        {ownerMatches.length === 0 && ownerQuery.trim() && <div className="cum-owner-empty">No users found</div>}
                        {ownerMatches.map(u => (
                          <button key={u.id} type="button" className="cum-owner-option" onClick={() => { setOwnerUser(u); setOwnerQuery(''); setShowOwnerSearch(false); }}>
                            <span className="cum-owner-avatar">{u.name[0].toUpperCase()}</span>
                            <div className="cum-owner-info">
                              <span className="cum-owner-name">{u.name}</span>
                              {u.email && <span className="cum-owner-email">{u.email}</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="sidebar-select-trigger-new has-icon-new cum-owner-trigger" onClick={() => setShowOwnerSearch(true)}>
                      {ownerUser ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-avatar cum-owner-avatar-sm">{ownerUser.name[0].toUpperCase()}</span>
                          <span>{ownerUser.name}</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="cum-owner-circle" />
                          <span style={{ color: '#94a3b8' }}>Not assigned</span>
                        </div>
                      )}
                      <svg className="sidebar-select-chevron-new" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Domain</label>
                <input className="sidebar-select-trigger-new cum-sidebar-text-input" placeholder="e.g. acme.com" value={domain} onChange={e => setDomain(e.target.value)} />
              </div>

              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Employee Size</label>
                <input className="sidebar-select-trigger-new cum-sidebar-text-input" type="number" placeholder="e.g. 500" value={empSize} onChange={e => setEmpSize(e.target.value)} />
              </div>

              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Headquarters</label>
                <input className="sidebar-select-trigger-new cum-sidebar-text-input" placeholder="e.g. San Francisco, CA" value={headquarters} onChange={e => setHq(e.target.value)} />
              </div>

              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Industry</label>
                <input className="sidebar-select-trigger-new cum-sidebar-text-input" placeholder="e.g. Technology" value={industry} onChange={e => setIndustry(e.target.value)} />
              </div>

              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Revenue</label>
                <input className="sidebar-select-trigger-new cum-sidebar-text-input" type="number" placeholder="e.g. 400000" value={revenue} onChange={e => setRevenue(e.target.value)} />
              </div>

              <div className="form-group-sidebar-new">
                <label className="sidebar-label-new">Website</label>
                <input className="sidebar-select-trigger-new cum-sidebar-text-input" placeholder="www.example.com" value={website} onChange={e => setWebsite(e.target.value)} />
              </div>

            </div>
          </div>

          {/* ── Footer ── */}
          <div className="task-modal-footer">
            <button className="cancel-btn" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="submit-btn" onClick={handleSave} disabled={saving || !name.trim()}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add company'}
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="cum-lightbox" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="attachment" className="cum-lightbox-img" onClick={e => e.stopPropagation()} />
          <button className="cum-lightbox-close" onClick={() => setLightboxUrl(null)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}
    </>
  );
};

export default CompanyModal;
